// Convex client + helpers (web). See the mobile
// counterpart at src/app/lib/convexDispatch.ts for the broader design
// notes. At module load we monkey-patch the shared convex client's
// `.action()` + `.mutation()` so any premium-shaped ConvexError
// (PREMIUM_REQUIRED / BOOKMARK_LIMIT_REACHED / DM_THREAD_LIMIT_REACHED)
// auto-pops the global PaywallSheet via the paywall bus. Callers still
// see the error rethrown — the bus fire is a side effect — so existing
// try/catch flows keep working and the paywall opens even if the call
// site didn't pre-check tier.

import type { User } from "./api";
import { convex as _convex } from "./convex";
import { requestPaywall, type PaywallTrigger } from "./paywall";
import { requestPublishReadiness } from "./publishReadiness";

// See src/app/lib/convexDispatch.ts for design notes. Code mirrors
// mobile exactly so the two clients pop the same trigger for the same
// backend error.
function mapCodeToTrigger(
  code: string | undefined,
  fnRef?: unknown,
): PaywallTrigger | undefined {
  switch (code) {
    case "ULTRA_REQUIRED":
      // The agent/developer platform is Ultra-gated. Any ULTRA_REQUIRED
      // (agent key mint, OAuth consent mint, webhook register) pops the
      // paywall on the agent-platform copy.
      return "agent_platform";
    case "BOOKMARK_LIMIT_REACHED":
      return "bookmark_limit";
    case "DM_THREAD_LIMIT_REACHED":
      return "dm_message_limit";
    case "RATE_LIMIT": {
      // The Discover swipe consumes `ratings.submitRating`; a free user
      // over the `discoverySwipeDaily` cap throws RATE_LIMIT.
      // Surface the swipe-cap upsell. Other RATE_LIMIT throwers (e.g.
      // geo's live-location throttle) carry a non-rating path and fall
      // through to no paywall — they're throttles, not premium upsells.
      const path = String(fnRef ?? "").toLowerCase();
      if (path.includes("rating")) return "discover_limit";
      return undefined;
    }
    case "PREMIUM_REQUIRED": {
      const path = String(fnRef ?? "").toLowerCase();
      // discovery.undoSwipe is premium-gated (requirePremium); the
      // discover_limit copy speaks to undo ("…and to undo a swipe").
      if (path.includes("discovery")) return "discover_limit";
      if (path.includes("dm") || path.includes("thread") || path.includes("message")) {
        return "dm_send";
      }
      return "subscribe";
    }
    default:
      return undefined;
  }
}

// Fire the right global UI side-effect for a thrown ConvexError:
// premium-shaped codes pop the paywall; NOT_PUBLISH_READY pops the
// publish-readiness modal. The error is still rethrown by the caller —
// these are pure side-effects, the safety net behind per-screen
// pre-checks. Mirrors mobile's convexDispatch.
function fireErrorSideEffect(e: unknown, fnRef?: unknown): void {
  const code = extractCode(e);
  if (code === "NOT_PUBLISH_READY") {
    requestPublishReadiness();
    return;
  }
  const trigger = mapCodeToTrigger(code, fnRef);
  if (trigger) requestPaywall({ trigger });
}

function extractCode(e: unknown): string | undefined {
  const d = (e as { data?: unknown })?.data;
  if (d && typeof d === "object" && "code" in d) {
    const c = (d as { code?: unknown }).code;
    if (typeof c === "string") return c;
  }
  const top = (e as { code?: unknown })?.code;
  return typeof top === "string" ? top : undefined;
}

let _patched = false;
function installPaywallInterceptor(client: typeof _convex): void {
  if (_patched) return;
  _patched = true;
  const origAction = client.action.bind(client);
  const origMutation = client.mutation.bind(client);
  client.action = (async (fnRef: unknown, args?: unknown, options?: unknown) => {
    try {
      return await (origAction as (...a: unknown[]) => Promise<unknown>)(fnRef, args, options);
    } catch (e) {
      fireErrorSideEffect(e, fnRef);
      throw e;
    }
  }) as typeof client.action;
  client.mutation = (async (fnRef: unknown, args?: unknown, options?: unknown) => {
    try {
      return await (origMutation as (...a: unknown[]) => Promise<unknown>)(fnRef, args, options);
    } catch (e) {
      fireErrorSideEffect(e, fnRef);
      throw e;
    }
  }) as typeof client.mutation;
}

installPaywallInterceptor(_convex);

export { convex } from "./convex";

let _currentUser: User | null = null;

export const currentUserStore = {
  get(): User | null {
    return _currentUser;
  },
  set(user: User | null): void {
    _currentUser = user;
  },
  clear(): void {
    _currentUser = null;
  },
};

export function convexSiteURL(): string {
  const explicit = (import.meta as ImportMeta & {
    env: { VITE_CONVEX_SITE_URL?: string };
  }).env.VITE_CONVEX_SITE_URL;
  if (explicit && explicit.length > 0) return explicit;
  return _convex.url.replace(/\.convex\.cloud$/, ".convex.site");
}
